/**
 * Sensitive Field Filter
 * 
 * Detects and filters sensitive form fields (passwords, credit cards, API keys)
 * to prevent them from being captured in DOM context or filled by the agent.
 */

const SENSITIVE_PATTERNS = {
    // Field types to always skip
    types: ['password'],
    
    // Field names/IDs containing these (partial match, case-insensitive)
    names: [
      // Payment
      'cc', 'card', 'credit', 'cvv', 'cvc', 'csc', 'expir',
      'cardnumber', 'card-number', 'card_number',
      'cardholder', 'card-holder', 'card_holder',
      
      // Auth & Secrets
      'password', 'pwd', 'passwd', 'pass',
      'secret', 'token', 'auth',
      
      // API Keys
      'api-key', 'apikey', 'api_key',
      'access-key', 'accesskey', 'access_key',
      'secret-key', 'secretkey', 'secret_key',
      'private-key', 'privatekey', 'private_key',
      'bearer', 'jwt',
      
      // Personal identifiers
      'ssn', 'social-security', 'social_security', 'socialsecurity',
      'tax-id', 'taxid', 'tax_id',
      'national-id', 'nationalid', 'national_id',
      
      // Generic sensitive
      'credential', 'private', 'secure'
    ],
    
    // Autocomplete attributes that indicate sensitive fields
    autocomplete: [
      'cc-number', 'cc-exp', 'cc-exp-month', 'cc-exp-year',
      'cc-csc', 'cc-name', 'cc-type',
      'current-password', 'new-password'
    ],
    
    // Data attributes that indicate sensitive fields
    dataAttributes: [
      'data-secret', 'data-sensitive', 'data-private',
      'data-api-key', 'data-token', 'data-auth'
    ]
  };
  
  /**
   * Check if a form field is sensitive and should be filtered
   * @param {HTMLElement} element - The form field element
   * @returns {boolean} - True if field is sensitive
   */
  export function isSensitiveField(element) {
    if (!element) return false;
    
    // Get field properties (lowercase for comparison)
    const type = (element.type || '').toLowerCase();
    const name = (element.name || '').toLowerCase();
    const id = (element.id || '').toLowerCase();
    const className = (element.className || '').toLowerCase();
    const autocomplete = (element.autocomplete || '').toLowerCase();
    const placeholder = (element.placeholder || '').toLowerCase();
    const ariaLabel = (element.getAttribute('aria-label') || '').toLowerCase();
    
    // Combine identifiers for pattern matching
    const identifiers = `${name} ${id} ${className} ${placeholder} ${ariaLabel}`;
    
    // Check 1: Field type
    if (SENSITIVE_PATTERNS.types.includes(type)) {
      return true;
    }
    
    // Check 2: Autocomplete attribute
    if (SENSITIVE_PATTERNS.autocomplete.includes(autocomplete)) {
      return true;
    }
    
    // Check 3: Name/ID/Class/Placeholder patterns
    for (const pattern of SENSITIVE_PATTERNS.names) {
      if (identifiers.includes(pattern)) {
        return true;
      }
    }
    
    // Check 4: Data attributes
    for (const attr of SENSITIVE_PATTERNS.dataAttributes) {
      if (element.hasAttribute(attr)) {
        return true;
      }
    }
    
    // Check 5: Input mode (for mobile)
    const inputMode = (element.inputMode || '').toLowerCase();
    if (inputMode === 'password') {
      return true;
    }
    
    return false;
  }
  
  /**
   * Check if a value looks like a secret/API key
   * @param {string} value - The value to check
   * @returns {boolean} - True if value looks sensitive
   */
  export function isSensitiveValue(value) {
    if (!value || typeof value !== 'string') return false;
    
    // Patterns that look like secrets
    const secretPatterns = [
      /^sk[-_]/i,                          // Stripe secret keys
      /^pk[-_]/i,                          // Stripe public keys
      /^api[-_]?key/i,                     // Generic API keys
      /^bearer\s+/i,                       // Bearer tokens
      /^token[-_]/i,                       // Generic tokens
      /^secret[-_]/i,                      // Generic secrets
      /^[a-f0-9]{32,}$/i,                  // Long hex strings (API keys, hashes)
      /^[A-Za-z0-9+/]{40,}={0,2}$/,        // Base64 encoded secrets
      /^ghp_[A-Za-z0-9]{36}$/,             // GitHub personal access tokens
      /^gho_[A-Za-z0-9]{36}$/,             // GitHub OAuth tokens
      /^xox[baprs]-[A-Za-z0-9-]+$/,        // Slack tokens
      /^eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/, // JWT tokens
    ];
    
    return secretPatterns.some(pattern => pattern.test(value.trim()));
  }
  
  /**
   * Get the reason why a field is considered sensitive (for debugging)
   * @param {HTMLElement} element - The form field element
   * @returns {string|null} - Reason string or null if not sensitive
   */
  export function getSensitiveReason(element) {
    if (!element) return null;
    
    const type = (element.type || '').toLowerCase();
    const name = (element.name || '').toLowerCase();
    const id = (element.id || '').toLowerCase();
    const autocomplete = (element.autocomplete || '').toLowerCase();
    
    if (SENSITIVE_PATTERNS.types.includes(type)) {
      return `type="${type}"`;
    }
    
    if (SENSITIVE_PATTERNS.autocomplete.includes(autocomplete)) {
      return `autocomplete="${autocomplete}"`;
    }
    
    const identifiers = `${name} ${id}`;
    for (const pattern of SENSITIVE_PATTERNS.names) {
      if (identifiers.includes(pattern)) {
        return `name/id contains "${pattern}"`;
      }
    }
    
    return null;
  }
  
  export default {
    isSensitiveField,
    isSensitiveValue,
    getSensitiveReason,
    SENSITIVE_PATTERNS
  };
